/**
 * Promo-code field renderer (CID-7209).
 *
 * Collapsed by default to a centered "I HAVE A PROMO CODE" link. Clicking it (or
 * arriving with a value already present, e.g. from the `promo_code` URL param)
 * reveals the input. The label and the helper description are both CMS-driven
 * (field.label / field.description).
 *
 * Validation happens on "Next", not on type: the renderer registers a
 * pre-submit validator that calls the public validate endpoint with whatever we
 * know so far (location + category + state). Per design we only surface state
 * when the code is invalid — an orange message that blocks advancing until the
 * user fixes or clears it.
 */
import {
  FormInput,
  Loader,
  Typography,
  useAlert,
} from "@chhjit/react-components";
import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useStore } from "effector-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import {
  $account,
  accountApi,
  leadUpdateFx,
} from "../../../../entities/account";
import { $appointment } from "../../../../entities/appointment";
import {
  $serviceLocation,
  useFeatureFlagsWithAttributes,
} from "../../../../entities/locations";
import { useFieldConfig } from "../../../context/step-config-context";
import { validatePromoCode } from "../../../api";
import { todayInLocationZone } from "../../../utils";
import {
  trackPromoCodeEntered,
  trackPromoCodeInvalid,
  trackPromoCodeValid,
} from "../../cdp";
import { usePreSubmitValidation } from "../pre-submit-validation";
import type { FieldRendererProps } from "./index";

const DEFAULT_INVALID_MESSAGE =
  "Sorry, this promo code is not valid for this job.";

const useStyles = makeStyles((theme) => ({
  revealRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
  },
  revealLink: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    fontWeight: "bold",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
  },
  helperRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

export const PromoCodeFieldRenderer = memo(
  ({ field, control }: FieldRendererProps) => {
    const styles = useStyles();
    const { register } = usePreSubmitValidation();
    const { getTextBlock } = useFieldConfig();

    // showAlert held in a ref so the stable validator can fire a red alert.
    const { showAlert } = useAlert();
    const showAlertRef = useRef(showAlert);
    showAlertRef.current = showAlert;

    // Tracks the last code persisted to the lead, to avoid redundant updates
    // (init "" so clearing when nothing was saved doesn't fire an empty update).
    const lastPersistedRef = useRef<string>("");

    // The lead-update transport switch, held in a ref for the stable validator (doc 07).
    const { toggles } = useFeatureFlagsWithAttributes();
    const leadUpdateOnNodeRef = useRef(false);
    leadUpdateOnNodeRef.current = Boolean(
      toggles?.HunkWare_Marketing_Automations_Lead_Sources_Node_Migration,
    );

    // A legacy coupon (coupon_code URL param) and a promo code can't be stacked.
    // When a coupon is applied, hide the promo field entirely and skip its check.
    const appointmentStore = useStore($appointment);
    const couponApplied = !!appointmentStore.appointment?.coupon?.id;
    const couponAppliedRef = useRef(couponApplied);
    couponAppliedRef.current = couponApplied;

    const { setValue } = useFormContext();
    const value = useWatch({ control, name: field.name });

    // Already-saved code from the store — used only to pre-fill the field (e.g.
    // promo_code URL param, or returning to this step). The field is NOT
    // auto-bound to the store; the validator writes it only when it should save.
    const initialStoreCodeRef = useRef(
      ($account.getState().account?.requestedPromoCode ?? "").toString(),
    );

    const [expanded, setExpanded] = useState(
      () =>
        !!(
          initialStoreCodeRef.current.trim() ||
          (value && String(value).trim())
        ),
    );
    const [invalidMessage, setInvalidMessage] = useState<string | null>(null);
    const [validating, setValidating] = useState(false);

    // Keep the latest typed value available to the (stable) validator.
    const valueRef = useRef(value);
    valueRef.current = value;

    // Seed the field once from the persisted store value (pre-fill).
    useEffect(() => {
      if (initialStoreCodeRef.current && !valueRef.current) {
        setValue(field.name, initialStoreCodeRef.current);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reveal-link text is CMS-driven via the field's `promoCodeRevealText` text
    // block (step- or block-level), falling back to a sensible default.
    const revealLabel = getTextBlock(
      "promoCodeRevealText",
      "I have a promo code",
    );

    // Invalid message: prefer the CMS `promoCodeInvalidText` when set, else the
    // API's message, else the default (resolved in the validator). Empty
    // fallback here lets us detect whether CMS actually provided a value. Held
    // in a ref so the (stable) validator reads the latest value.
    const sanityInvalidText = getTextBlock("promoCodeInvalidText", "").trim();
    const sanityInvalidTextRef = useRef(sanityInvalidText);
    sanityInvalidTextRef.current = sanityInvalidText;

    // CMS opt-out: when set, skip on-Next validation and let the user through.
    // The code is still saved to the lead, and junk jobs still validate + apply
    // (or note) it at booking time — that path is unaffected by this flag.
    const skipValidationRef = useRef(!!field.skipValidation);
    skipValidationRef.current = !!field.skipValidation;

    const validate = useCallback(async () => {
      // Placement-independent: when a lead already exists, mirror the saved
      // value onto the lead too (deduped). When the field is on/before the
      // contact step the lead isn't created yet — tokens/generate captures the
      // store value at creation.
      const persistToLead = (savedCode: string) => {
        const { account: acct, token, lead } = $account.getState();
        const locId = $serviceLocation.getState().location?.id;
        if (
          lead?.id &&
          acct?.id &&
          locId &&
          token &&
          savedCode !== lastPersistedRef.current
        ) {
          lastPersistedRef.current = savedCode;
          leadUpdateFx({
            locationId: locId,
            accountId: acct.id,
            leadId: lead.id,
            token,
            payload: { requestedPromoCode: savedCode },
            useNode: leadUpdateOnNodeRef.current,
          }).catch(() => {
            /* non-blocking */
          });
        }
      };
      // Save the code (→ accountCreate/accountUpdate payload + lead).
      const save = (savedCode: string) => {
        accountApi.set({ requestedPromoCode: savedCode });
        persistToLead(savedCode);
      };
      // Never persist an unsaved/invalid code; clear any prior saved value.
      const clearSaved = () => {
        accountApi.set({ requestedPromoCode: "" });
        persistToLead("");
      };

      // A coupon takes precedence — the field is hidden; never save a promo.
      if (couponAppliedRef.current) {
        setInvalidMessage(null);
        clearSaved();
        return true;
      }

      const code = (valueRef.current ?? "").toString().trim();
      if (!code) {
        setInvalidMessage(null);
        clearSaved();
        return true;
      }

      // CMS skip mode: don't validate, but still save the code as-is.
      if (skipValidationRef.current) {
        setInvalidMessage(null);
        save(code);
        return true;
      }

      const { location } = $serviceLocation.getState();
      const locationId = location?.id;
      // Can't validate without a resolved location — let the user through, but
      // do NOT save (only validated-valid codes are saved when validation is on).
      if (!locationId) {
        setInvalidMessage(null);
        return true;
      }

      const account = $account.getState().account;
      setValidating(true);
      try {
        const result = await validatePromoCode({
          promoCode: code,
          locationId,
          jobCategoryId: account?.category?.id,
          state: account?.billingAddress?.state || undefined,
          creationDate: todayInLocationZone(location?.timeZone?.shortName),
        });
        if (result.valid) {
          setInvalidMessage(null);
          save(code);
          trackPromoCodeValid(code);
          return true;
        }
        // Invalid: never save it, surface the message (CMS → API → default),
        // fire a red alert, and block advancing.
        clearSaved();
        const apiMessage = result.message?.trim();
        const displayMessage =
          sanityInvalidTextRef.current || apiMessage || DEFAULT_INVALID_MESSAGE;
        setInvalidMessage(displayMessage);
        showAlertRef.current(displayMessage, { variant: "error" });
        trackPromoCodeInvalid(code, apiMessage || displayMessage);
        return false;
      } finally {
        setValidating(false);
      }
    }, []);

    useEffect(() => register(validate), [register, validate]);

    // Clear any prior invalid state as soon as the user edits the code.
    const isFirstRender = useRef(true);
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      setInvalidMessage(null);
    }, [value]);

    // Hide entirely when a legacy coupon is already applied.
    if (couponApplied) {
      return null;
    }

    if (!expanded) {
      return (
        <Box className={styles.revealRow}>
          <Typography
            variant="caption"
            className={styles.revealLink}
            onClick={() => {
              setExpanded(true);
              trackPromoCodeEntered();
            }}
            id={`ob-form-${field.name}-reveal`}
            role="button"
          >
            {revealLabel}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <FormInput
          id={`ob-form-${field.name}`}
          control={control}
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          error={!!invalidMessage}
        />
        {(validating || invalidMessage || field.description) && (
          <Box className={styles.helperRow}>
            {validating && <Loader size="small" />}
            {invalidMessage ? (
              <Typography variant="caption" color="error.main">
                {invalidMessage}
              </Typography>
            ) : (
              field.description && (
                <Typography variant="caption" color="text.secondary">
                  {field.description}
                </Typography>
              )
            )}
          </Box>
        )}
      </Box>
    );
  },
);
